import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import cloneDeep from 'lodash/cloneDeep';
import compact from 'lodash/compact';
import isEqual from 'lodash/isEqual';
import last from 'lodash/last';
import { StateCreator } from 'zustand';

import { progressSteps } from './constants/progressSteps';
import { ProgressState } from './types/ProgressState';
import { ProgressStep } from './types/ProgressStep';

export const progressStore: StateCreator<ProgressState> = (set, get) => ({
  computed: {
    get steps(): ProgressStep[] {
      return cloneDeep(progressSteps);
    },
  },

  getCurrentStep: (index: number): ProgressStep => {
    const {
      computed: { steps },
    } = get();

    return steps[index];
  },
  getCurrentStepIndex: (
    status: ChecklightSessionStatus | string | null | undefined,
  ): number => {
    const {
      computed: { steps },
    } = get();

    if (!status) {
      return 0;
    }

    return steps.findIndex((step): boolean =>
      step.applicationStatuses.includes(status),
    );
  },
  getNextStep: (index: number): ProgressStep => {
    const {
      computed: { steps },
    } = get();

    return index < steps.length - 1 ? steps[index + 1] : null;
  },
  getPrevStep: (index: number): ProgressStep => {
    const {
      computed: { steps },
    } = get();

    return index > 0 ? steps[index - 1] : null;
  },

  /**
   * Retrieves the preview steps based on the application status.
   * If the application status is provided, the function marks the previous step as completed
   * and returns the current step and the next step.
   * If the application status is not provided, the function returns an empty array.
   */
  getPreviewStepsByApplicationStatus: (
    status: ChecklightSessionStatus | string | null | undefined,
  ): ProgressStep[] => {
    const { getCurrentStep, getCurrentStepIndex, getPrevStep, getNextStep } =
      get();

    // find the current step index
    const currentStepIndex = getCurrentStepIndex(status);

    // current step could not be found
    if (currentStepIndex === -1) {
      return [];
    }

    const prevStep = getPrevStep(currentStepIndex);
    const currentStep = getCurrentStep(currentStepIndex);
    const nextStep = getNextStep(currentStepIndex);

    // assign previous step to be completed
    if (prevStep) {
      prevStep.completed = true;
    }

    // assign current step to be completed
    if (currentStep) {
      currentStep.completed = true;
    }

    return compact([prevStep, currentStep, nextStep]);
  },

  /**
   * Retrieves the progress steps based on the application status.
   * If the application status is provided, the function marks the previous steps as completed
   * and marks the current step as completed if it is the last step.
   * If the application status is not provided, the function returns all the progress steps.
   */
  getStepsByApplicationStatus: (
    status: ChecklightSessionStatus | string | null | undefined,
  ): ProgressStep[] => {
    const {
      computed: { steps },
      getCurrentStepIndex,
    } = get();

    // find the current step index
    const currentStepIndex = getCurrentStepIndex(status);

    // mark all previous steps and current step as completed
    steps.slice(0, currentStepIndex + 1).forEach((step): void => {
      step.completed = true;
    });

    return steps;
  },

  isLastStep: (step: ProgressStep): boolean => {
    const { steps } = get().computed;
    const lastStep = last(steps);

    if (!lastStep) {
      return false;
    }

    return isEqual(step.applicationStatuses, lastStep.applicationStatuses);
  },
});
